import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ChevronRight, ChevronLeft, Check } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMOTIONS_LIST = [
    { name: 'Tristeza', emoji: '😔' },
    { name: 'Raiva', emoji: '😠' },
    { name: 'Ansiedade', emoji: '😰' },
    { name: 'Medo', emoji: '😨' },
    { name: 'Frustração', emoji: '😞' },
    { name: 'Culpa', emoji: '😣' },
    { name: 'Vergonha', emoji: '😖' },
    { name: 'Irritação', emoji: '😤' },
    { name: 'Desânimo', emoji: '🙁' },
    { name: 'Alegria', emoji: '😊' },
    { name: 'Gratidão', emoji: '🙏' },
    { name: 'Esperança', emoji: '🌟' }
];

const BODY_SENSATIONS = [
    'Coração acelerado',
    'Tensão muscular',
    'Respiração alterada',
    'Aperto no peito',
    'Calor/Frio',
    'Tremor',
    'Dor de cabeça',
    'Estômago embrulhado'
];

const REACTIONS = [
    'Fiquei em silêncio',
    'Acelerei a fala',
    'Me afastei',
    'Tentei agradar',
    'Confrontei',
    'Evitei',
    'Chorei',
    'Gritei'
];

const EmotionDiaryWizard = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        situation: '',
        emotions: [],
        body_sensations: [],
        body_sensations_other: '',
        reactions: [],
        reactions_other: '',
        automatic_thoughts: [''],
        vulnerabilities: {
            sleep_deprived: false,
            hungry: false,
            overloaded: false,
            difficult_day: false,
            multitasking: false,
            other: ''
        }
    });

    const steps = [
        {
            title: 'Descrever a situação',
            component: <Step1Situation formData={formData} setFormData={setFormData} />
        },
        {
            title: 'O que você sentiu?',
            component: <Step2Emotions formData={formData} setFormData={setFormData} />
        },
        {
            title: 'Corpo e reação',
            component: <Step3BodyReactions formData={formData} setFormData={setFormData} />
        },
        {
            title: 'O que passou pela sua cabeça?',
            component: <Step4Thoughts formData={formData} setFormData={setFormData} />
        },
        {
            title: 'Como você estava antes?',
            component: <Step5Vulnerabilities formData={formData} setFormData={setFormData} />
        }
    ];

    const canProceed = () => {
        switch (currentStep) {
            case 0: return formData.situation.trim().length > 10;
            case 1: return formData.emotions.length > 0;
            case 2: return formData.body_sensations.length > 0 || formData.reactions.length > 0;
            case 3: return formData.automatic_thoughts.some(t => t.trim().length > 0);
            case 4: return true; // Optional step
            default: return false;
        }
    };

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            handleSubmit();
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);

        try {
            // Prepare data
            const submitData = {
                situation: formData.situation,
                emotions: formData.emotions,
                body_sensations: [
                    ...formData.body_sensations,
                    ...(formData.body_sensations_other ? [formData.body_sensations_other] : [])
                ],
                reactions: [
                    ...formData.reactions,
                    ...(formData.reactions_other ? [formData.reactions_other] : [])
                ],
                automatic_thoughts: formData.automatic_thoughts.filter(t => t.trim()),
                vulnerabilities: formData.vulnerabilities
            };

            const response = await axios.post(
                `${API}/emotion-diary-v2/entries`,
                submitData,
                { withCredentials: true }
            );

            // Check if should generate insights
            if (response.data.should_generate_insights) {
                navigate('/diary/insights');
            } else {
                navigate('/diary/history');
            }
        } catch (error) {
            console.error('Error saving diary entry:', error);
            alert('Erro ao salvar registro. Tente novamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white py-8">
            <div className="max-w-3xl mx-auto px-4">
                {/* Welcome Message (only on first step) */}
                {currentStep === 0 && (
                    <Card className="p-6 mb-6 bg-purple-50 border-purple-200">
                        <p className="text-gray-800 leading-relaxed">
                            Este é um espaço para observar o que acontece com você quando emoções ficam intensas.
                        </p>
                        <p className="text-gray-700 mt-2">
                            <strong>Não é um teste, não é um diagnóstico.</strong> É um treino de percepção.
                        </p>
                    </Card>
                )}

                {/* Progress Bar */}
                <div className="mb-6">
                    <div className="flex justify-between mb-2">
                        {steps.map((step, index) => (
                            <div
                                key={index}
                                className={`flex-1 h-2 mx-1 rounded ${index <= currentStep ? 'bg-purple-600' : 'bg-gray-200'
                                    }`}
                            />
                        ))}
                    </div>
                    <p className="text-sm text-gray-600 text-center">
                        Passo {currentStep + 1} de {steps.length}: {steps[currentStep].title}
                    </p>
                </div>

                {/* Current Step */}
                <Card className="p-8">
                    {steps[currentStep].component}
                </Card>

                {/* Navigation */}
                <div className="flex justify-between mt-6">
                    <Button
                        onClick={handleBack}
                        variant="outline"
                        disabled={currentStep === 0}
                    >
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        Voltar
                    </Button>

                    <Button
                        onClick={handleNext}
                        disabled={!canProceed() || isSubmitting}
                        className="bg-purple-600 hover:bg-purple-700"
                    >
                        {currentStep === steps.length - 1 ? (
                            <>
                                {isSubmitting ? 'Salvando...' : 'Concluir'}
                                <Check className="w-4 h-4 ml-2" />
                            </>
                        ) : (
                            <>
                                Próximo
                                <ChevronRight className="w-4 h-4 ml-2" />
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};

// Step 1: Situation
const Step1Situation = ({ formData, setFormData }) => (
    <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
            O que aconteceu pouco antes da emoção ficar intensa?
        </h2>
        <p className="text-gray-600 mb-4">
            Onde você estava? Com quem? O que estava sendo dito ou feito?
        </p>

        <Textarea
            value={formData.situation}
            onChange={(e) => setFormData({ ...formData, situation: e.target.value })}
            placeholder="Descreva a situação..."
            className="min-h-[150px]"
        />

        <p className="text-sm text-purple-600 mt-3 italic">
            💡 Descreva como uma câmera registraria. Sem explicar, sem justificar.
        </p>
    </div>
);

// Step 2: Emotions
const Step2Emotions = ({ formData, setFormData }) => {
    const toggleEmotion = (emotionName) => {
        const exists = formData.emotions.find(e => e.name === emotionName);

        if (exists) {
            setFormData({
                ...formData,
                emotions: formData.emotions.filter(e => e.name !== emotionName)
            });
        } else {
            setFormData({
                ...formData,
                emotions: [...formData.emotions, { name: emotionName, intensity: 5 }]
            });
        }
    };

    const updateIntensity = (emotionName, intensity) => {
        setFormData({
            ...formData,
            emotions: formData.emotions.map(e =>
                e.name === emotionName ? { ...e, intensity: parseInt(intensity) } : e
            )
        });
    };

    return (
        <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Quais emoções você percebeu?
            </h2>
            <p className="text-gray-600 mb-4">Pode marcar mais de uma</p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                {EMOTIONS_LIST.map((emotion) => {
                    const isSelected = formData.emotions.find(e => e.name === emotion.name);

                    return (
                        <button
                            key={emotion.name}
                            onClick={() => toggleEmotion(emotion.name)}
                            className={`p-4 rounded-lg border-2 transition-all ${isSelected
                                    ? 'border-purple-600 bg-purple-50'
                                    : 'border-gray-200 hover:border-purple-300'
                                }`}
                        >
                            <div className="text-3xl mb-2">{emotion.emoji}</div>
                            <div className="text-sm font-medium">{emotion.name}</div>
                        </button>
                    );
                })}
            </div>

            {/* Intensity Sliders */}
            {formData.emotions.length > 0 && (
                <div className="mt-6">
                    <h3 className="font-semibold text-gray-900 mb-4">
                        Quão intensas elas estavam?
                    </h3>

                    {formData.emotions.map((emotion) => (
                        <div key={emotion.name} className="mb-4">
                            <div className="flex justify-between mb-2">
                                <span className="text-sm font-medium">{emotion.name}</span>
                                <span className="text-sm text-purple-600 font-bold">
                                    {emotion.intensity}/10
                                </span>
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="10"
                                value={emotion.intensity}
                                onChange={(e) => updateIntensity(emotion.name, e.target.value)}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// Step 3: Body & Reactions
const Step3BodyReactions = ({ formData, setFormData }) => {
    const toggleBodySensation = (sensation) => {
        if (formData.body_sensations.includes(sensation)) {
            setFormData({
                ...formData,
                body_sensations: formData.body_sensations.filter(s => s !== sensation)
            });
        } else {
            setFormData({
                ...formData,
                body_sensations: [...formData.body_sensations, sensation]
            });
        }
    };

    const toggleReaction = (reaction) => {
        if (formData.reactions.includes(reaction)) {
            setFormData({
                ...formData,
                reactions: formData.reactions.filter(r => r !== reaction)
            });
        } else {
            setFormData({
                ...formData,
                reactions: [...formData.reactions, reaction]
            });
        }
    };

    return (
        <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
                O que aconteceu no seu corpo e na sua reação?
            </h2>

            {/* Body Sensations */}
            <div className="mb-8">
                <h3 className="font-semibold text-gray-900 mb-3">
                    O que você percebeu no corpo?
                </h3>

                <div className="space-y-2">
                    {BODY_SENSATIONS.map((sensation) => (
                        <label key={sensation} className="flex items-center space-x-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={formData.body_sensations.includes(sensation)}
                                onChange={() => toggleBodySensation(sensation)}
                                className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                            />
                            <span className="text-gray-700">{sensation}</span>
                        </label>
                    ))}

                    <input
                        type="text"
                        value={formData.body_sensations_other}
                        onChange={(e) => setFormData({ ...formData, body_sensations_other: e.target.value })}
                        placeholder="Outro..."
                        className="mt-2 w-full p-2 border border-gray-300 rounded"
                    />
                </div>
            </div>

            {/* Reactions */}
            <div>
                <h3 className="font-semibold text-gray-900 mb-3">
                    O que você fez ou teve vontade de fazer?
                </h3>

                <div className="space-y-2">
                    {REACTIONS.map((reaction) => (
                        <label key={reaction} className="flex items-center space-x-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={formData.reactions.includes(reaction)}
                                onChange={() => toggleReaction(reaction)}
                                className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                            />
                            <span className="text-gray-700">{reaction}</span>
                        </label>
                    ))}

                    <input
                        type="text"
                        value={formData.reactions_other}
                        onChange={(e) => setFormData({ ...formData, reactions_other: e.target.value })}
                        placeholder="Outro..."
                        className="mt-2 w-full p-2 border border-gray-300 rounded"
                    />
                </div>
            </div>
        </div>
    );
};

// Step 4: Thoughts
const Step4Thoughts = ({ formData, setFormData }) => {
    const addThought = () => {
        setFormData({
            ...formData,
            automatic_thoughts: [...formData.automatic_thoughts, '']
        });
    };

    const updateThought = (index, value) => {
        const newThoughts = [...formData.automatic_thoughts];
        newThoughts[index] = value;
        setFormData({ ...formData, automatic_thoughts: newThoughts });
    };

    const removeThought = (index) => {
        if (formData.automatic_thoughts.length > 1) {
            setFormData({
                ...formData,
                automatic_thoughts: formData.automatic_thoughts.filter((_, i) => i !== index)
            });
        }
    };

    return (
        <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Que frase, ideia ou imagem passou pela sua cabeça nesse momento?
            </h2>

            <p className="text-sm text-purple-600 mb-6 italic">
                💡 Se sua mente estivesse legendando a cena, qual seria a legenda?
            </p>

            <div className="space-y-4">
                {formData.automatic_thoughts.map((thought, index) => (
                    <div key={index} className="flex space-x-2">
                        <Textarea
                            value={thought}
                            onChange={(e) => updateThought(index, e.target.value)}
                            placeholder="Ex: 'Preciso dar conta sozinha', 'Se eu falar, vai piorar'..."
                            className="flex-1"
                        />
                        {formData.automatic_thoughts.length > 1 && (
                            <Button
                                onClick={() => removeThought(index)}
                                variant="outline"
                                size="sm"
                                className="text-red-600"
                            >
                                ✕
                            </Button>
                        )}
                    </div>
                ))}
            </div>

            <Button
                onClick={addThought}
                variant="outline"
                className="mt-4"
            >
                + Adicionar outro pensamento
            </Button>
        </div>
    );
};

// Step 5: Vulnerabilities
const Step5Vulnerabilities = ({ formData, setFormData }) => {
    const toggleVulnerability = (key) => {
        setFormData({
            ...formData,
            vulnerabilities: {
                ...formData.vulnerabilities,
                [key]: !formData.vulnerabilities[key]
            }
        });
    };

    return (
        <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Como você estava antes disso acontecer?
            </h2>

            <p className="text-sm text-purple-600 mb-6 italic">
                💡 Isso não explica tudo, mas ajuda a entender o contexto.
            </p>

            <div className="space-y-3">
                <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={formData.vulnerabilities.sleep_deprived}
                        onChange={() => toggleVulnerability('sleep_deprived')}
                        className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <span className="text-gray-700">Dormi pouco?</span>
                </label>

                <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={formData.vulnerabilities.hungry}
                        onChange={() => toggleVulnerability('hungry')}
                        className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <span className="text-gray-700">Estava com fome?</span>
                </label>

                <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={formData.vulnerabilities.overloaded}
                        onChange={() => toggleVulnerability('overloaded')}
                        className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <span className="text-gray-700">Estava sobrecarregada(o)?</span>
                </label>

                <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={formData.vulnerabilities.difficult_day}
                        onChange={() => toggleVulnerability('difficult_day')}
                        className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <span className="text-gray-700">Já vinha de um dia difícil?</span>
                </label>

                <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={formData.vulnerabilities.multitasking}
                        onChange={() => toggleVulnerability('multitasking')}
                        className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <span className="text-gray-700">Tentando dar conta de muitas tarefas?</span>
                </label>

                <div className="mt-4">
                    <label className="block text-gray-700 mb-2">Outro:</label>
                    <input
                        type="text"
                        value={formData.vulnerabilities.other}
                        onChange={(e) => setFormData({
                            ...formData,
                            vulnerabilities: { ...formData.vulnerabilities, other: e.target.value }
                        })}
                        placeholder="Descreva..."
                        className="w-full p-2 border border-gray-300 rounded"
                    />
                </div>
            </div>
        </div>
    );
};

export default EmotionDiaryWizard;

